import { useMemo, useRef, useState } from 'react';
import Button from '@/components/base/Button';
import StatCard from '@/components/base/StatCard';
import { useRole } from '@/context/RoleContext';
import { useBrand } from '@/context/BrandContext';
import {
  brandPresets,
  logoAssets,
} from '@/mocks/branding';
import { brandCompleteness } from '@/utils/branding';
import type { BrandPreset } from '@/types/portal';
import Modal from '@/components/base/Modal';
import AgencyProfileCard from './components/AgencyProfileCard';
import BrandColourCard from './components/BrandColourCard';
import DomainCard from './components/DomainCard';
import BrandPreviewPanel from './components/BrandPreviewPanel';
import LogoAssetsPanel from './components/LogoAssetsPanel';
import BrandSurfacePanel from './components/BrandSurfacePanel';
import { brandColours } from '@/mocks/branding';
import { api } from '@/utils/api';

export default function Branding() {
  const { can } = useRole();
  const canManage = can('settings.manage');

  const {
    profile,
    colours,
    surfaces,
    logos,
    dirty,
    updateField,
    setMarkets,
    applyColour,
    applyPreset,
    toggleSurface,
    setLogo,
    clearLogo,
    save: ctxSave,
    reset,
    resetToDefaults,
  } = useBrand();

  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [domainError, setDomainError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  const showError = (err: unknown, fallback: string) => {
    setNotice('');
    setError(err instanceof Error ? err.message : fallback);
    // The banner sits under the stat cards; make sure it's on screen.
    requestAnimationFrame(() => bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  const completeness = useMemo(() => brandCompleteness(profile), [profile]);

  const presetName = useMemo(() => {
    const match = brandPresets.find(
      (preset) =>
        preset.primary === colours.primary &&
        preset.accent === colours.accent &&
        preset.secondary === colours.secondary,
    );
    return match ? match.name : 'Custom palette';
  }, [colours]);

  const verifyDomain = async () => {
    setDomainError('');
    try {
      await api('/settings/branding/verify-domain', { method: 'POST' });
      setNotice('Domain verified — SSL is active and the portal is live on your own domain.');
    } catch (err) {
      setDomainError(err instanceof Error ? err.message : 'Could not verify the domain.');
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await ctxSave();
      setNotice('Branding saved — the sidebar and all platform surfaces reflect your changes immediately.');
    } catch (err) {
      showError(err, 'Could not save the branding.');
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    reset();
    setNotice('');
    setError('');
  };

  const handleReset = async () => {
    setSaving(true);
    setError('');
    try {
      await resetToDefaults();
      setNotice('Branding reset to platform defaults.');
    } catch (err) {
      showError(err, 'Could not reset the branding.');
    } finally {
      setSaving(false);
      setShowResetConfirm(false);
    }
  };

  const handlePreset = (preset: BrandPreset) => {
    applyPreset(preset);
  };

  const handleLogoUpload = (id: string, dataUrl: string) => {
    setLogo(id, dataUrl);
    const asset = logoAssets.find((entry) => entry.id === id);
    setNotice(`${asset?.name ?? 'Logo'} updated — your new mark now appears in the sidebar and live preview.`);
  };

  const handleLogoRemove = (id: string) => {
    clearLogo(id);
    const asset = logoAssets.find((entry) => entry.id === id);
    setNotice(`${asset?.name ?? 'Logo'} removed — the monogram initials are back in place.`);
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <p className="text-xs font-label font-medium uppercase tracking-wider text-foreground-400">
            Settings
          </p>
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Branding
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Your agency profile, logo, colours and client-facing domain.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="ghost"
            icon="ri-refresh-line"
            disabled={!canManage}
            onClick={() => setShowResetConfirm(true)}
          >
            Reset defaults
          </Button>
          <Button
            variant="outline"
            icon="ri-arrow-go-back-line"
            disabled={!dirty || !canManage || saving}
            onClick={cancel}
          >
            Discard
          </Button>
          <Button icon="ri-save-3-line" disabled={!dirty || !canManage || saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Profile completeness"
          value={`${completeness.percent}%`}
          icon="ri-percent-line"
          tone="primary"
          hint={`${completeness.filled} of ${completeness.total} core fields complete`}
        />
        <StatCard
          label="Brand palette"
          value={presetName}
          icon="ri-palette-line"
          tone="accent"
          hint="Primary, accent & secondary applied"
        />
        <StatCard
          label="Logo assets"
          value={`${logoAssets.length} files`}
          icon="ri-image-line"
          tone="secondary"
          hint="Mark, wordmark & portal avatar"
        />
        <StatCard
          label="Client domain"
          value={profile.domainStatus}
          icon="ri-global-line"
          tone="neutral"
          hint={profile.domain || 'No domain configured'}
        />
      </div>

      {notice ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {error ? (
        <div
          ref={bannerRef}
          role="alert"
          className="mt-5 flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-2.5"
        >
          <span className="inline-flex items-center gap-2 text-sm text-primary-800">
            <i className="ri-error-warning-line" />
            {error}
          </span>
          <button
            type="button"
            onClick={() => setError('')}
            className="cursor-pointer text-primary-700 hover:text-primary-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {!canManage ? (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
          <i className="ri-lock-line" />
          Your current role can view branding but not change it. Switch to an Agency Administrator to
          edit the profile, colours or domain.
        </div>
      ) : null}

      <Modal
        open={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        title="Reset to platform defaults?"
        subtitle="This will restore the platform colours, logos and surface settings."
        icon="ri-refresh-line"
        widthClassName="max-w-md"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowResetConfirm(false)}>
              Cancel
            </Button>
            <Button icon="ri-refresh-line" disabled={saving} onClick={() => void handleReset()}>
              {saving ? 'Resetting…' : 'Yes, reset defaults'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground-600">
          Any unsaved changes and any previously saved colours, logos and surface settings
          will be overwritten. Your agency profile and domain are kept. You can
          re-customise and save again at any time.
        </p>
      </Modal>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <AgencyProfileCard
            profile={profile}
            canEdit={canManage}
            onField={updateField}
            onSetMarkets={setMarkets}
          />
          <BrandColourCard
            colours={colours}
            palette={brandColours}
            canEdit={canManage}
            onColour={applyColour}
            onPreset={handlePreset}
          />
          <DomainCard
            domain={profile.domain}
            status={profile.domainStatus}
            canEdit={canManage}
            error={domainError}
            onDomain={(value) => {
              setDomainError('');
              updateField('domain', value);
            }}
            onVerify={() => void verifyDomain()}
          />
        </div>

        <div className="sticky top-6 space-y-5">
          <BrandPreviewPanel profile={profile} colours={colours} presetName={presetName} logos={logos} />
          <LogoAssetsPanel
            initials={profile.logoInitials}
            primary={colours.primary}
            assets={logoAssets}
            logos={logos}
            canEdit={canManage}
            onUpload={handleLogoUpload}
            onRemove={handleLogoRemove}
          />
          <BrandSurfacePanel
            surfaces={surfaces}
            canEdit={canManage}
            onToggle={toggleSurface}
          />
        </div>
      </div>
    </div>
  );
}